import instance from '../../../api/axios'
import uploadFile from '../../Files/api/uploadFile'
import removeFile from '../../Files/api/removeFile'
import {
  AnalyzedIncome,
  CreateIncome,
  CreateIncomeInput,
  Income,
  IncomeDocumentFile,
  IncomeListRequest,
  PaginatedIncomesResponse,
  UpdateIncome,
  UpdateIncomeInput,
} from '../types/income'

export const loadIncomes = async (): Promise<Income[]> => {
  const response = await instance.get('/income')
  return response.data
}

export const loadIncomeList = async (
  request: IncomeListRequest,
): Promise<PaginatedIncomesResponse> => {
  const params: Record<string, string> = {
    page: String(request.page),
    limit: String(request.limit || 25),
  }

  if (request.filters.name.trim()) {
    params.name = request.filters.name.trim()
  }

  if (request.filters.startDate.trim()) {
    params.startDate = request.filters.startDate.trim()
  }

  if (request.filters.endDate.trim()) {
    params.endDate = request.filters.endDate.trim()
  }

  const response = await instance.get('/income/list', { params })
  return response.data
}

export const saveIncome = async (income: CreateIncome): Promise<Income> => {
  const response = await instance.post('/income', income)
  return response.data
}

export const modifyIncome = async (income: UpdateIncome): Promise<Income> => {
  const response = await instance.put('/income', income)
  return response.data
}

export const removeIncome = async (id: string): Promise<string> => {
  const response = await instance.delete(`/income/${id}`)
  return response.data
}

export const analyzePayslip = async (file: File): Promise<AnalyzedIncome> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await instance.post('/income/analyze-payslip', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

const uniqueStrings = (values: Array<string | null | undefined>): string[] =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value))))

const getIncomeDocumentIds = (income: Income): string[] =>
  uniqueStrings([
    ...(income.documentFiles || []).map((file) => file.id),
    income.documentFileId,
  ])

const toDocumentFiles = (
  files: { id: string; name?: string; type?: string }[],
): IncomeDocumentFile[] =>
  files.map((file) => ({
    id: file.id,
    name: file.name,
    mimeType: file.type,
  }))

const toUpdateIncome = (income: Income): UpdateIncome => ({
  id: income.id,
  incomeDate: income.incomeDate,
  name: income.name,
  description: income.description || undefined,
  kind: income.kind,
  status: income.status,
  amount: income.amount,
  grossAmount: income.grossAmount ?? undefined,
  currency: income.currency,
  category: income.category || undefined,
  documentFileId: income.documentFileId || undefined,
  documentFileName: income.documentFileName || undefined,
  documentMimeType: income.documentMimeType || undefined,
  documentFiles: income.documentFiles || undefined,
})

const stripDocumentChange = <
  T extends {
    documentFile?: File
    removeDocument?: boolean
  },
>(
  payload: T,
): Omit<T, 'documentFile' | 'removeDocument'> => {
  const { documentFile: _documentFile, removeDocument: _removeDocument, ...income } =
    payload
  return income
}

export const syncIncomeDocument = async (
  income: Income,
  options: { file?: File; remove?: boolean } = {},
): Promise<Income> => {
  const previousIds = getIncomeDocumentIds(income)
  const incoming = options.file ? [options.file] : []

  if (incoming.length) {
    const uploaded: Array<{ id: string; name?: string; type?: string }> = []
    for (const file of incoming) {
      uploaded.push(
        await uploadFile({
          file,
          name: file.name,
          description: '',
          isPublic: false,
          incomeId: income.id,
        }),
      )
    }

    const primary = uploaded[0]
    const updated = await modifyIncome({
      ...toUpdateIncome(income),
      documentFileId: primary.id,
      documentFileName: primary.name || incoming[0].name,
      documentMimeType: primary.type || incoming[0].type,
      documentFiles: toDocumentFiles(uploaded),
    })

    await Promise.all(
      previousIds
        .filter((id) => !uploaded.some((file) => file.id === id))
        .map((id) => removeFile(id).catch(() => undefined)),
    )

    return updated
  }

  if (options.remove && previousIds.length) {
    const updated = await modifyIncome({
      ...toUpdateIncome(income),
      documentFileId: undefined,
      documentFileName: undefined,
      documentMimeType: undefined,
      documentFiles: [],
    })

    await Promise.all(
      previousIds.map((id) => removeFile(id).catch(() => undefined)),
    )

    return updated
  }

  return income
}

export const saveIncomeWithDocument = async (
  payload: CreateIncomeInput,
): Promise<Income> => {
  const saved = await saveIncome(stripDocumentChange(payload))
  if (!saved?.id) {
    throw new Error('Income was not saved')
  }

  return syncIncomeDocument(saved, {
    file: payload.documentFile,
    remove: payload.removeDocument,
  })
}

export const modifyIncomeWithDocument = async (
  payload: UpdateIncomeInput,
): Promise<Income> => {
  const saved = await modifyIncome(stripDocumentChange(payload))
  if (!saved?.id) {
    throw new Error('Income was not saved')
  }

  return syncIncomeDocument(saved, {
    file: payload.documentFile,
    remove: payload.removeDocument,
  })
}
